import type {
  CandidateSpan,
  ConvertFeature,
  ConvertResult,
  DiagnosticTraceStep,
  Draft,
  HistoryEntry,
  LanguagePreference,
  SupportedLanguageCode,
  TrialHandoffSnapshot,
} from "@fluentry/domain";

export type ActiveResultSource =
  | { kind: "live" }
  | { kind: "history"; historyEntryId: string }
  | { kind: "trial-handoff" };

export type DraftUnloadSaveState = "idle" | "dirty" | "saving" | "saved" | "error";

export const WORKBENCH_SECTION_IDS = {
  drafts: "drafts",
  history: "history",
} as const;

type WorkbenchSectionId = (typeof WORKBENCH_SECTION_IDS)[keyof typeof WORKBENCH_SECTION_IDS];

type RestoredDocumentState = {
  feature: ConvertFeature;
  document: string;
  interpretedIntent?: string;
  targetOutput?: string;
  candidateState: CandidateSpan[];
  diagnosticTrace?: DiagnosticTraceStep[];
  confidence?: number;
  explanation?: string;
  nativeLanguagePreference: LanguagePreference;
  targetLanguagePreference: LanguagePreference;
  followInputLanguage: boolean;
  resolvedNativeLanguage?: SupportedLanguageCode;
  resolvedTargetLanguage?: SupportedLanguageCode;
};

// Drafts and history entries always carry language/feature values: the domain schema fills them with
// defaults at parse time, so the output types are non-optional. Read them directly instead of papering
// over a "missing" case that the contract guarantees cannot happen.
export function historyEntryToDocumentState(entry: HistoryEntry): RestoredDocumentState {
  return {
    feature: entry.feature,
    document: entry.sourceInput,
    interpretedIntent: entry.interpretedIntent,
    targetOutput: entry.targetOutput,
    candidateState: entry.alternatives,
    diagnosticTrace: entry.diagnosticTrace,
    confidence: entry.confidence,
    explanation: entry.explanation,
    nativeLanguagePreference: entry.nativeLanguage,
    targetLanguagePreference: entry.targetLanguage,
    followInputLanguage: entry.followInputLanguage,
    resolvedNativeLanguage: entry.resolvedNativeLanguage,
    resolvedTargetLanguage: entry.resolvedTargetLanguage,
  };
}

// A live convert result has no preference fields of its own, so the caller passes the preferences it
// actually ran with rather than the surface assuming a fixed pair.
export function convertResultToDocumentState(
  result: ConvertResult,
  preferences: {
    nativeLanguagePreference: LanguagePreference;
    targetLanguagePreference: LanguagePreference;
    followInputLanguage: boolean;
  },
): RestoredDocumentState {
  return {
    feature: result.feature,
    document: result.sourceInput,
    interpretedIntent: result.interpretedIntent,
    targetOutput: result.targetOutput,
    candidateState: result.alternatives,
    diagnosticTrace: result.diagnosticTrace,
    confidence: result.confidence,
    explanation: result.explanation,
    nativeLanguagePreference: preferences.nativeLanguagePreference,
    targetLanguagePreference: preferences.targetLanguagePreference,
    followInputLanguage: preferences.followInputLanguage,
    resolvedNativeLanguage: result.resolvedNativeLanguage,
    resolvedTargetLanguage: result.resolvedTargetLanguage,
  };
}

export function draftToDocumentState(entry: Draft): RestoredDocumentState {
  return {
    feature: entry.feature,
    document: entry.sourceInput,
    interpretedIntent: entry.interpretedIntent,
    targetOutput: entry.targetOutput,
    candidateState: entry.alternatives,
    diagnosticTrace: entry.diagnosticTrace,
    explanation: entry.explanation,
    nativeLanguagePreference: entry.nativeLanguage,
    targetLanguagePreference: entry.targetLanguage,
    followInputLanguage: entry.followInputLanguage,
    resolvedNativeLanguage: entry.resolvedNativeLanguage,
    resolvedTargetLanguage: entry.resolvedTargetLanguage,
  };
}

export function trialHandoffSnapshotToDocumentState(
  snapshot: TrialHandoffSnapshot,
): RestoredDocumentState {
  return {
    feature: snapshot.feature,
    document: snapshot.sourceInput,
    interpretedIntent: snapshot.interpretedIntent,
    targetOutput: snapshot.targetOutput,
    candidateState: snapshot.alternatives,
    diagnosticTrace: snapshot.diagnosticTrace,
    confidence: snapshot.confidence,
    explanation: snapshot.explanation,
    nativeLanguagePreference: snapshot.nativeLanguage,
    targetLanguagePreference: snapshot.targetLanguage,
    followInputLanguage: snapshot.followInputLanguage,
    resolvedNativeLanguage: snapshot.resolvedNativeLanguage,
    resolvedTargetLanguage: snapshot.resolvedTargetLanguage,
  };
}

export function isSubmitShortcut(event: {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey?: boolean;
  altKey?: boolean;
}): boolean {
  return (
    event.key === "Enter" && !event.shiftKey && !event.altKey && (event.metaKey || event.ctrlKey)
  );
}

export function formatHistoryTimestamp(createdAt: string, locale?: string) {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) {
    return createdAt;
  }

  return new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function getWorkbenchStatus(args: {
  loading: boolean;
  error: string | null;
  candidateCount: number;
  activeResultSource: ActiveResultSource | null;
}) {
  if (args.loading) {
    return {
      tone: "loading" as const,
      label: "Reading draft",
      detail: "Fluentry is mapping the draft into highlighted ranges you can review in place.",
    };
  }

  if (args.error) {
    return {
      tone: "error" as const,
      label: "Run failed",
      detail: args.error,
    };
  }

  if (args.candidateCount > 0 && args.activeResultSource?.kind === "history") {
    return {
      tone: "history" as const,
      label: "History restored",
      detail: "Saved recovery options were restored locally without rerunning the API.",
    };
  }

  if (args.candidateCount > 0 && args.activeResultSource?.kind === "trial-handoff") {
    return {
      tone: "history" as const,
      label: "Trial resumed",
      detail: "Your public trial result was carried into the workbench and will autosave here.",
    };
  }

  if (args.candidateCount > 0) {
    return {
      tone: "success" as const,
      label: "Guidance ready",
      detail: "Click a highlight to inspect replacement options and rationale.",
    };
  }

  return {
    tone: "idle" as const,
    label: "Ready",
    detail: "Compose a draft and run Fluentry when you are ready.",
  };
}

export function shouldBlockDraftUnload(args: {
  shouldPersistActiveDraft: boolean;
  draftSaveState: DraftUnloadSaveState;
  draftMatchesCurrentSnapshot: boolean;
}) {
  if (!args.shouldPersistActiveDraft) {
    return false;
  }

  return (
    args.draftSaveState === "dirty" ||
    args.draftSaveState === "saving" ||
    args.draftSaveState === "error" ||
    !args.draftMatchesCurrentSnapshot
  );
}

export function resolveWorkbenchSectionFromHash(hash: string): WorkbenchSectionId | null {
  const normalizedHash = hash.trim().toLowerCase();

  if (normalizedHash === `#${WORKBENCH_SECTION_IDS.drafts}`) {
    return WORKBENCH_SECTION_IDS.drafts;
  }

  if (normalizedHash === `#${WORKBENCH_SECTION_IDS.history}`) {
    return WORKBENCH_SECTION_IDS.history;
  }

  return null;
}
